// Axial hex coordinate system (flat-top orientation)
// Reference: https://www.redblobgames.com/grids/hexagons/
// Image dimensions (full PNG size including transparent padding) — 150 DPI tiles
export const HEX_IMAGE_WIDTH = 466;
export const HEX_IMAGE_HEIGHT = 405;
// Grid spacing size — controls how tightly tiles pack together.
// Lower = tighter, higher = further apart. Baseline from artwork measurement: 121 (at 150 DPI).
export const HEX_SIZE = 115;
export function hexKey(h) {
    return `${h.q},${h.r}`;
}
export function parseHexKey(key) {
    const [q, r] = key.split(",").map(Number);
    return { q, r };
}
// Flat-top axial to pixel (center of hex)
export function hexToPixel(h) {
    const x = HEX_SIZE * (3 / 2) * h.q;
    const y = HEX_SIZE * ((Math.sqrt(3) / 2) * h.q + Math.sqrt(3) * h.r);
    return { x, y };
}
// Pixel to nearest axial hex (flat-top)
export function pixelToHex(px, py) {
    const q = ((2 / 3) * px) / HEX_SIZE;
    const r = ((-1 / 3) * px + (Math.sqrt(3) / 3) * py) / HEX_SIZE;
    return axialRound(q, r);
}
function axialRound(q, r) {
    const s = -q - r;
    let rq = Math.round(q);
    let rr = Math.round(r);
    const rs = Math.round(s);
    const qDiff = Math.abs(rq - q);
    const rDiff = Math.abs(rr - r);
    const sDiff = Math.abs(rs - s);
    if (qDiff > rDiff && qDiff > sDiff) {
        rq = -rr - rs;
    }
    else if (rDiff > sDiff) {
        rr = -rq - rs;
    }
    return {
        q: Object.is(rq, -0) ? 0 : rq,
        r: Object.is(rr, -0) ? 0 : rr,
    };
}
// 6 neighbors in axial coordinates (flat-top)
const DIRECTIONS = [
    { q: 1, r: 0 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: -1, r: 0 },
    { q: -1, r: 1 },
    { q: 0, r: 1 },
];
export function hexNeighbors(h) {
    return DIRECTIONS.map((d) => ({ q: h.q + d.q, r: h.r + d.r }));
}
// Manhattan distance in cube coordinates
export function hexDistance(a, b) {
    const dq = a.q - b.q;
    const dr = a.r - b.r;
    const ds = -a.q - a.r - (-b.q - b.r);
    return Math.max(Math.abs(dq), Math.abs(dr), Math.abs(ds));
}
const DELTA_TO_EDGE = {
    "0,-1": "N",
    "1,-1": "NE",
    "1,0": "SE",
    "0,1": "S",
    "-1,1": "SW",
    "-1,0": "NW",
};
export const OPPOSITE_EDGE = {
    N: "S",
    NE: "SW",
    SE: "NW",
    S: "N",
    SW: "NE",
    NW: "SE",
};
export function getEdgeBetween(from, to) {
    const dq = to.q - from.q;
    const dr = to.r - from.r;
    return DELTA_TO_EDGE[`${dq},${dr}`] ?? null;
}
// All hexes within radius of origin (0,0)
export function hexesInRadius(radius) {
    const results = [];
    for (let q = -radius; q <= radius; q++) {
        const r1 = Math.max(-radius, -q - radius);
        const r2 = Math.min(radius, -q + radius);
        for (let r = r1; r <= r2; r++) {
            results.push({ q, r });
        }
    }
    return results;
}
// All hexes filling a screen-aligned rectangle centered on origin (0,0).
// halfCols and halfRows are counts of hex columns/rows on each side of center
// in odd-q offset coordinates. Total hex count = (2*halfCols+1) * (2*halfRows+1).
//
// For flat-top axial, hex y(q,r) = HEX_SIZE * sqrt(3) * (r + q/2). To keep each
// column's row range centered on screen-y = 0, shift r by -floor(q/2).
export function hexesInRect(halfCols, halfRows) {
    const results = [];
    for (let q = -halfCols; q <= halfCols; q++) {
        const offset = Math.floor(q / 2);
        for (let row = -halfRows; row <= halfRows; row++) {
            results.push({ q, r: row - offset });
        }
    }
    return results;
}
